import { useQuery } from '@tanstack/react-query';
import * as userManagementApi from '@/services/userManagementApi';
import type { UserManagementDTO } from '@/types/api';

export interface DirectoryUser {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  department: string;
  title: string;
  status: 'active' | 'inactive';
  lastLogin: string;
  provider: string;
  customer: string;
  groups: string[];
  externalId: string;
  managerEmail?: string;
  attributes: Record<string, any>;
  syncedAt: string;
}

function mapUserToDirectoryUser(u: UserManagementDTO): DirectoryUser {
  const roleNames = (u.roles || []).map(r => typeof r === 'string' ? r : r.name);
  return {
    id: u.id || '',
    username: u.username || u.email || '',
    email: u.email || '',
    firstName: u.firstname || u.firstName || '',
    lastName: u.lastname || u.lastName || '',
    department: '',
    title: '',
    status: 'active',
    lastLogin: u.created_at || new Date().toISOString(),
    provider: u.is_ldap_user ? 'LDAP' : 'Platform',
    customer: u.tenantId || 'Unknown',
    groups: roleNames,
    externalId: u.id || '',
    managerEmail: undefined,
    attributes: {},
    syncedAt: u.created_at || new Date().toISOString(),
  };
}

export const useDirectoryUsers = () => {
  const query = useQuery({
    queryKey: ['directoryUsers'],
    queryFn: async () => {
      const users = await userManagementApi.listUsers();
      return (users || [])
        .filter(u => u.is_ldap_user === true)
        .map(mapUserToDirectoryUser);
    },
  });

  return {
    users: query.data || [],
    isLoading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
  };
};
